import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson } from "../../../context/api";

/**
 * Mesas del club (CRUO F1) — sección de Ubicaciones SOLO para social_club.
 * Las mesas viven en service_tables (separadas del stock, ARQUITECTURA_CARRITO_V2
 * §2.2): aquí se crean en tanda, se dan de baja y se IMPRIMEN sus QRs.
 * El QR codifica el deep-link que fijará la mesa en la PWA del socio.
 */
export default function MesasSection() {
  const [token, , , venueID] = useContext(UserContext);
  const [mesas, setMesas] = useState([]);
  const [cuantas, setCuantas] = useState(4);
  const [zona, setZona] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await apiFetch(`/v1/service-tables?venue_id=${venueID}`, { headers });
      if (res.ok) setMesas((await safeJson(res)) || []);
    } catch {
      /* silencioso */
    }
  }, [token, venueID, headers]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crear = useCallback(async () => {
    const n = Math.max(1, Math.min(50, Number(cuantas) || 0));
    const start = mesas.length + 1;
    const labels = Array.from({ length: n }, (_, i) => `Mesa ${start + i}`);
    setBusy(true);
    setMsg("");
    try {
      const res = await apiFetch(`/v1/service-tables`, {
        method: "POST",
        headers,
        body: JSON.stringify({ venue_id: venueID, labels, zone: zona.trim() || null }),
      });
      if (!res.ok) throw new Error("No se pudieron crear las mesas");
      await cargar();
      setMsg(`${n} mesa${n === 1 ? "" : "s"} creada${n === 1 ? "" : "s"} ✓`);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  }, [cuantas, zona, mesas.length, venueID, headers, cargar]);

  const borrar = useCallback(
    async (mesa) => {
      if (!window.confirm(`¿Dar de baja ${mesa.label}? Su QR dejará de funcionar.`)) return;
      await apiFetch(`/v1/service-tables/${mesa.id}?venue_id=${venueID}`, { method: "DELETE", headers }).catch(() => {});
      cargar();
    },
    [venueID, headers, cargar]
  );

  // Deep-link del QR: la PWA del socio resolverá /mesa/<token>. Origen actual del FE.
  const qrUrl = (mesa) => `${window.location.origin}/mesa/${mesa.qr_token}`;

  const imprimir = useCallback(() => {
    window.print();
  }, []);

  return (
    <section className="rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h2 className="text-lg font-semibold text-[#0A0A0A]">Mesas del club</h2>
          <p className="text-xs text-[#737373]">
            Cada mesa lleva su QR: el socio lo escanea y pide desde su móvil. Sin stock — solo dónde se sirve.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            max={50}
            value={cuantas}
            onChange={(e) => setCuantas(e.target.value)}
            className="h-9 w-20 rounded-md border border-[#E5E5E5] px-2 text-sm"
            title="Cuántas mesas crear"
          />
          <input
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            placeholder="Zona (opcional)"
            className="h-9 w-36 rounded-md border border-[#E5E5E5] px-2 text-sm"
          />
          <button
            type="button"
            disabled={busy}
            onClick={crear}
            className="h-9 rounded-md bg-[#0A0A0A] px-3 text-sm font-semibold text-white hover:bg-[#262626] disabled:bg-[#E5E5E5]"
          >
            Crear mesas
          </button>
          {mesas.length > 0 && (
            <button
              type="button"
              onClick={imprimir}
              className="h-9 rounded-md bg-lime-500 px-3 text-sm font-semibold text-white hover:bg-lime-600"
            >
              🖨️ Imprimir QRs
            </button>
          )}
        </div>
      </div>

      {msg && <div className="mb-2 text-xs text-[#737373] print:hidden">{msg}</div>}

      {mesas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E5E5E5] p-6 text-center text-sm text-[#A3A3A3] print:hidden">
          Aún no hay mesas. Crea la tanda y imprime sus QRs.
        </div>
      ) : (
        /* En pantalla: tarjetas compactas. Al imprimir: una cuadrícula limpia de QRs. */
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 print:grid-cols-2 print:gap-8">
          {mesas.map((m) => (
            <div
              key={m.id}
              className="flex flex-col items-center rounded-xl border border-[#E5E5E5] p-3 text-center print:break-inside-avoid print:border-2 print:p-8"
            >
              <div className="bg-white p-1 print:p-2">
                <QRCode value={qrUrl(m)} size={96} className="print:h-40 print:w-40" />
              </div>
              <div className="mt-2 text-sm font-semibold text-[#0A0A0A] print:text-2xl">{m.label}</div>
              {m.zone && <div className="text-[11px] text-[#737373] print:text-base">{m.zone}</div>}
              <button
                type="button"
                onClick={() => borrar(m)}
                className="mt-1.5 text-[11px] text-[#DC2626] hover:underline print:hidden"
              >
                Dar de baja
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
